import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/nexus-utilities';
import { environment } from '../../environments/environment';

/**
 * Mirrors Sierra.Finance.Api.Models.Entities.Costing.PoolStatus. Numeric on the
 * wire to match System.Text.Json's default enum serialization.
 */
export enum PoolStatus {
  Pending = 0,
  Computing = 1,
  Ready = 2,
  Failed = 3,
}

/** One row in <c>PoolsStatusDto.pools</c>. */
export interface PoolStatusEntry {
  name: PoolName;
  status?: PoolStatus | null;
  computedAt?: string | null;
  computedBy?: string | null;
  lastError?: string | null;
}

export interface PoolsStatusDto {
  year: number;
  month: number;
  pools: PoolStatusEntry[];
}

/**
 * Names match the strings the backend emits in <c>PoolStatusEntry.Name</c>
 * (see CostingPoolsController.GetPoolsStatus). Keep this list in sync with the
 * compute endpoint route segments below.
 */
export type PoolName = 'Depreciation' | 'Utility' | 'Consumables' | 'Labor' | 'Chemicals' | 'RawMaterial' | 'Scrap';

/** Maps the PoolName onto the kebab-case route segment used by the API. */
export const POOL_ROUTE_SEGMENT: Readonly<Record<PoolName, string>> = {
  Depreciation: 'depreciation',
  Utility: 'utility',
  Consumables: 'consumables',
  Labor: 'labor',
  Chemicals: 'chemicals',
  RawMaterial: 'raw-material',
  Scrap: 'scrap',
};

export const ALL_POOL_NAMES: readonly PoolName[] = ['Depreciation', 'Utility', 'Consumables', 'Labor', 'Chemicals', 'RawMaterial', 'Scrap'];

/**
 * Body for every per-pool compute endpoint. Same shape as the calculate
 * commands — Year/Month are required; exchange rates are required by the
 * Depreciation/Labor/Utility/Chemicals/RawMaterial pools that emit USD-
 * denominated rates and converted from HNL.
 */
export interface ComputePoolCommand {
  year: number;
  month: number;
  exchangeRate: number;
  exchangeRateEurUsd: number;
}

/**
 * Pool entity as returned by GET /api/v1/costing-pools/{poolName}/{year}/{month}.
 * Each concrete pool extends this with its own rate breakdown — kept loose
 * here because the panel only renders the common metadata.
 */
export interface MonthlyPoolBase {
  id?: string;
  year: number;
  month: number;
  status: PoolStatus;
  computedAt?: string | null;
  computedBy?: string | null;
  lastError?: string | null;
}

@Injectable({ providedIn: 'root' })
export class CostingPoolsProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/costing-pools';

  /** Aggregate readiness for all six pools for the period. */
  public getStatus(year: number, month: number): Observable<ApiResponse<PoolsStatusDto>> {
    return this.httpClient.get<ApiResponse<PoolsStatusDto>>(`${this.baseUrl}/status/${year}/${month}`);
  }

  /**
   * Compute (or recompute) a single pool. Each pool's compute endpoint takes
   * the same body shape; we route by <paramref name="pool"/> via
   * <c>POOL_ROUTE_SEGMENT</c> so adding a pool only touches the map above.
   */
  public compute(pool: PoolName, cmd: ComputePoolCommand): Observable<ApiResponse<MonthlyPoolBase>> {
    const segment = POOL_ROUTE_SEGMENT[pool];
    return this.httpClient.post<ApiResponse<MonthlyPoolBase>>(`${this.baseUrl}/${segment}/compute`, cmd);
  }
}
